import {
    asyncListWithSkipTakeCreate,
    cachedValueCreateWithParamsV2,
    Evaluable,
    evaluateWhenFunction,
    hashFunctionCreate,
    stringReplaceEmptyOrWhiteSpaceValue,
    valueConvertWhenNotNullable
} from '@wix/devzai-utils-common';
import type {DoppeDtoEvent, DoppeDtoEventRegistrationType} from '../../../client-server-common/types/doppe-dto-event';
import type {IDoppeViewerApi} from '../../../client-server-common/api/doppe-viewer-api';

/**
 * Creates a cached factory for fetching events async lists.
 * The factory caches async lists by search and registrationType parameters,
 * so multiple calls with the same params return the same cached list.
 * 
 * @param viewerApiClientGetter - A getter function for the viewer API client
 * @returns A cached value factory for events async lists
 * 
 * @example
 * ```ts
 * const eventsAsyncListFactory = useMemo(() => {
 *     return doppeViewerAsyncListFactoryEvents(() => viewerApiClient);
 * }, [viewerApiClient]);
 * 
 * const eventsAsyncList = eventsAsyncListFactory.getValue({
 *     search: searchText,
 *     registrationType: DoppeDtoEventRegistrationType.Ticketing
 * });
 * ```
 */
export function doppeViewerAsyncListFactoryEvents(
    viewerApiClientGetter: Evaluable<() => IDoppeViewerApi>
) {
    return cachedValueCreateWithParamsV2({
        paramsHashFunction: hashFunctionCreate<doppeViewerAsyncListFactoryEvents.Params>({
            search: hashFunctionCreate(),
            registrationType: hashFunctionCreate(),
            pastEventsOnly: hashFunctionCreate(),
        }),
        paramsNormalizationFunction: params => ({
            ...params,
            search: valueConvertWhenNotNullable(params.search, s =>
                stringReplaceEmptyOrWhiteSpaceValue(s, undefined)
            )
        }),
        valueFunction: (params) => {
            const viewerApiClient = evaluateWhenFunction(viewerApiClientGetter);

            return asyncListWithSkipTakeCreate<DoppeDtoEvent>({
                fetchFunction: async (skip, take) => {
                    return viewerApiClient.getEvents({
                        skip,
                        take,
                        search: params.search,
                        registrationType: params.registrationType,
                        pastEventsOnly: params.pastEventsOnly,
                    });
                },
                defaultTake: 30,
            });
        }
    });
}

export namespace doppeViewerAsyncListFactoryEvents {
    export interface Params {
        /**
         * Search query to filter events by name.
         */
        search?: string;
        
        /**
         * Filter events by registration type (RSVP, Ticketing).
         */
        registrationType?: DoppeDtoEventRegistrationType;

        /**
         * When true, fetches only past (ended) events instead of upcoming events.
         */
        pastEventsOnly?: boolean;
    }
}
